import React, { useState, useRef } from 'react';
import styles from './index.module.less';
import cn from 'classnames';
import { Divider } from 'antd';
import { ISpuInfo, IVariant } from 'base/interfaces/product';
import useAddCart from '@base/product/useAddCart';
import { ProdPrice } from '@app/common/ProductDetailPopup/ProdPrice';
import { ProdReviews } from '@app/common/ProductDetailPopup/ProdReviews';
import { ProdViewMore } from '@app/common/ProductDetailPopup/ProdViewMore';
import ProductSellingPoints from '@app/product/ProductSellingPoints';
import ProductDescription from '@app/product/ProductDescription';
import KitProducts from '@app/product/KitProducts';
import { hasSpec, isShownDetailElement } from 'base/utils/product';
import ProductImagesLightbox from '@app/product/ProductImagesLightbox';
import ProductReviewList from '@app/product/ProductReviewList';
import { IProdTabItem, ProdTabs } from '@app/common/ProductDetailPopup/ProdTabs';
import AddToCart from '@app/product/AddToCart';
import { ProdTitle } from '@base/product/ProdTitle';
import { ITransInstance, useTrans } from 'base/utils/i18n';
import { IReviewPage } from 'mod/review/interface';
import { ProdVat } from 'mod/product/ProdVat';
import { ProdSku } from 'mod/product/ProdSku';

export const DEFAULT_ELEMENTS = [
    'pictures',
    'name',
    'price',
    'reviews',
    'sku',
    'addCartForm',
    'qty',
    'aboutItem',
    'spec',
    'bundle',
    'viewMore',
    'btns',
];

export type TDetailElement = typeof DEFAULT_ELEMENTS[number];

export type IProductDetailProps = {
    variantId: string;
    spu: ISpuInfo;
    initialReviewPage?: IReviewPage;
    elements?: TDetailElement[];
    hiddenElements?: TDetailElement[];
};

const THUMB_IMAGE_SIZE = 555; //商品展示图的尺寸

type IGetTabListParams = {
    shownVariant: IVariant;
    elements?: TDetailElement[];
    hiddenElements?: TDetailElement[];
    t: ITransInstance['t'];
    initialReviewPage?: IReviewPage;
};

const getTabList = (params: IGetTabListParams): IProdTabItem[] => {
    const { shownVariant, elements, hiddenElements, t, initialReviewPage } = params;
    let list: IProdTabItem[] = [];
    const isKit = shownVariant.kitProductVariantList.length > 0;
    const isShown = (element: string): boolean => isShownDetailElement(element, elements, hiddenElements);

    // 有 bundle list 不展示 Technical Specs
    if (!isKit && hasSpec(shownVariant) && isShown('spec')) {
        // 技术文档
        list.push({
            key: 'spec',
            title: t('web_product_tab_technical_specs'),
            content: <ProductDescription shownVariant={shownVariant}/>
        })
    }

    if (isKit && isShown('bundle')) {
        // bundle list
        list.push({
            key: 'bundle',
            title: t('web_product_tab_bundle_list'),
            content: <KitProducts shownVariant={shownVariant} canJump={false}/>
        })
    }

    if (isShown('reviews') && shownVariant.review && Number(shownVariant.review.score) > 0) {
        // 评论
        list.push({
            key: 'review',
            title: t('web_product_tab_reviews', { num: shownVariant.review.reviewNum }),
            content: (
                <ProductReviewList
                    variant={shownVariant}
                    from={'modal'}
                    id={'review-list'}
                    initialReviewPage={initialReviewPage}
                />
            )
        });
    }
    // 记录位置
    list = list.map((item, index) => {
        return {
            ...item,
            positionIndex: index,
        }
    })

    return list;
};

export const ProductDetail: React.FC<IProductDetailProps> = React.memo(props => {
    const { spu, initialReviewPage, variantId, elements, hiddenElements = [] } = props;
    const addCart = useAddCart({
        spuInfo: spu,
        changeUrlWhenCheckOption: false,
    });
    const { shownVariant } = addCart;
    const containerRef = useRef<HTMLDivElement>(null);
    const [tabActiveIndex, setTabActiveIndex] = useState<number>(0)
    const { t } = useTrans();

    const isShown = (element: string): boolean => isShownDetailElement(element, elements, hiddenElements);

    return (
        <div ref={containerRef} className={styles.container}>
            <div className={cn(styles['grid-top'], 'flex')}>
                {isShown('pictures') && (
                    <div className={styles['grid-top-left']}>
                        <ProductImagesLightbox
                            shownVariant={shownVariant}
                            thumbImageSize={THUMB_IMAGE_SIZE}
                        />
                        {isShown('viewMore') && (
                            <ProdViewMore shownVariant={shownVariant}/>
                        )}
                    </div>
                )}

                <div className={cn(styles['grid-top-right'], 'flex-1')}>
                    {isShown('name') && (
                        <ProdTitle shownVariant={shownVariant}/>
                    )}

                    {(isShown('price') || isShown('reviews')) && (
                        <div className={cn(
                            styles['price-reviews'],
                            'flex flex-middle'
                        )}>
                            {isShown('price') && (
                                <div className={'flex-1 flex flex-middle'}>
                                    <ProdPrice shownVariant={shownVariant}/>
                                    {shownVariant.vat && <ProdVat className={styles.vat}/>}
                                </div>
                            )}

                            {isShown('reviews') && (
                                <ProdReviews
                                    shownVariant={shownVariant}
                                    onClick={() => {
                                        const reivewList = document.querySelector('#review-list') as HTMLDivElement;
                                        if (!reivewList) {
                                            return;
                                        }
                                        reivewList.closest('.ant-modal-wrap')?.scrollTo({
                                            top: reivewList.getBoundingClientRect().top,
                                            behavior: "smooth"
                                        })
                                    }}
                                />
                            )}
                        </div>
                    )}

                    {isShown('sku') && (
                        <ProdSku
                            className={styles['prod-sku']}
                            shownVariant={shownVariant}
                        />
                    )}

                    {isShown('addCartForm') && (
                        <AddToCart
                            className={styles['add-cart']}
                            addCart={addCart}
                            isQuickView
                            shownVariant={shownVariant}
                            qtyShown={isShown('qty')}
                            btnsShown={isShown('btns')}
                        />
                    )}

                    {isShown('aboutItem') && (
                        <>
                            <Divider style={{ margin: '30px 0 30px' }}/>
                            <ProductSellingPoints shownVariant={shownVariant}/>
                        </>
                    )}
                </div>
            </div>

            <ProdTabs
                tabList={getTabList({
                    shownVariant,
                    elements,
                    hiddenElements,
                    t,
                    initialReviewPage
                })}
                shownVariant={shownVariant}
                activeIndex={tabActiveIndex}
                setActiveIndex={setTabActiveIndex}
            />

            {isShown('viewMore') && !isShown('pictures') && (
                <ProdViewMore shownVariant={shownVariant}/>
            )}
        </div>
    )
});